import type { SupabaseClient } from '../client'
import { type AccessLevel, highestLevel } from '../constants'
import type { ClientProjectAccess } from '../domain/client-project-access'
import { AccessDeniedError } from '../errors'
import { unwrapList } from './_helpers'

/**
 * Lista os projetos acessíveis para um client_user, um por projeto.
 *
 * O banco guarda uma linha por escopo, então quem entra em três iniciativas do
 * mesmo projeto tem três linhas. Sem juntar aqui, o projeto apareceria três
 * vezes no seletor. O nível que sobra é o maior.
 */
export async function listAccessibleProjectIds(
  client: SupabaseClient,
  clientUserId: string,
): Promise<{ project_id: number; access_level: AccessLevel }[]> {
  const result = await client
    .schema('portal')
    .from('client_project_access')
    .select('project_id, access_level')
    .eq('client_user_id', clientUserId)

  const linhas = unwrapList(result) as unknown as {
    project_id: number
    access_level: AccessLevel
  }[]

  const porProjeto = new Map<number, AccessLevel[]>()
  for (const l of linhas) {
    const atual = porProjeto.get(l.project_id) ?? []
    atual.push(l.access_level)
    porProjeto.set(l.project_id, atual)
  }

  return [...porProjeto.entries()].map(([project_id, niveis]) => ({
    project_id,
    access_level: highestLevel(niveis) as AccessLevel,
  }))
}

/**
 * O acesso desta pessoa a este projeto, se houver.
 *
 * Devolve a linha de maior nível. Era `maybeSingle()` e quebrava desde que o
 * acesso passou a poder ser por iniciativa: com duas linhas, o PostgREST
 * responde erro, e o guard de rota lia isso como "sem acesso" e barrava quem
 * tinha acesso de verdade.
 */
export async function getAccessForProject(
  client: SupabaseClient,
  clientUserId: string,
  projectId: number,
): Promise<ClientProjectAccess | null> {
  const linhas = await listAccessForProject(client, clientUserId, projectId)
  if (linhas.length === 0) return null

  const maior = highestLevel(linhas.map((l) => l.access_level as AccessLevel))
  return linhas.find((l) => l.access_level === maior) ?? linhas[0] ?? null
}

/** Todas as linhas de acesso da pessoa naquele projeto, uma por escopo. */
export async function listAccessForProject(
  client: SupabaseClient,
  clientUserId: string,
  projectId: number,
): Promise<ClientProjectAccess[]> {
  const result = await client
    .schema('portal')
    .from('client_project_access')
    .select('*')
    .eq('client_user_id', clientUserId)
    .eq('project_id', projectId)
  return unwrapList(result) as unknown as ClientProjectAccess[]
}

/**
 * Garante que o cliente tem acesso ao projeto, ou lança `AccessDeniedError`.
 */
export async function requireProjectAccess(
  client: SupabaseClient,
  clientUserId: string,
  projectId: number,
): Promise<AccessLevel> {
  const access = await getAccessForProject(client, clientUserId, projectId)
  if (!access) throw new AccessDeniedError(`No access to project ${projectId}`)
  return access.access_level as AccessLevel
}
